import { useState, useEffect, useMemo } from "react";
import {
    TablePagination,
    Button,
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Typography,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import Table from "../../../components/Table";
import ActionsMenu from "../../../components/ActionsMenu";
import CategoryModal from "./components/CategoryModal";
import { CategoryColumns } from "./components/CategoryColumns";
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import api from "../../../utils/api";

export default function Categories() {
    const columns = useMemo(() => CategoryColumns(), []);

    const [search, setSearch] = useState("");
    const [parentFilter, setParentFilter] = useState("");
    const [parentOptions, setParentOptions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [row, setRow] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        api.get("admin/categories/list").then((res) => {
            setParentOptions(res.data.data || []);
        }).catch(() => setParentOptions([]));
    }, []);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;
        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;
        setLoading(true);
        try {
            const response = await api.get("admin/categories", {
                params: {
                    filter: filters,
                    page: currentPage,
                    perPage: currentPerPage,
                },
            });
            setPagination({
                page: currentPage,
                perPage: currentPerPage,
                total: response.data.meta.pagination.total,
                totalPages: response.data.meta.pagination.totalPages,
            });
            setRows(response.data.data);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRows = async (rowsToDelete) => {
        try {
            for (const id of rowsToDelete) {
                await api.delete(`admin/categories/${id}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.error(err);
        }
    };

    const handleOpenManageRow = (r) => {
        setRow(r ?? null);
        setIsModalOpen(true);
    };

    const handleCloseManageRow = (refetch) => {
        setRow(null);
        setIsModalOpen(false);
        if (refetch) fetchRows(activeFilters);
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = { search: search };
            if (parentFilter !== '') newFilters.parent_id = parentFilter === 'null' ? 'null' : parentFilter;
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search, parentFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const getCategoryTitle = (r) => r?.title_en || r?.title_ru || r?.title_hy || 'Category';

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Categories (Courses)
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <FormControl sx={{ width: { xs: '100%', md: '32ch' } }} variant="outlined" size="small">
                            <OutlinedInput
                                size="small"
                                placeholder="Search by title..."
                                sx={{ background: "#fff" }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                        <FormControl sx={{ minWidth: 160 }} size="small">
                            <InputLabel>Parent</InputLabel>
                            <Select
                                value={parentFilter}
                                label="Parent"
                                onChange={(e) => setParentFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="null">None (root)</MenuItem>
                                {parentOptions.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.title_en || cat.title_ru || cat.title_hy || cat.id}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenManageRow()}
                    >
                        Create New
                    </Button>
                </Box>

                <Table
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    selactable={false}
                    draggable={false}
                    renderActions={(r) => (
                        <ActionsMenu
                            actions={[
                                { label: "Edit", onClick: () => handleOpenManageRow(r) },
                                {
                                    label: "Delete",
                                    onClick: () => handleDeleteRows([r.id]),
                                    confirm: true,
                                    confirmText: `Are you sure you want to delete "${getCategoryTitle(r)}"?`,
                                },
                            ]}
                        />
                    )}
                />

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(e, newPage) => handlePageChange(newPage)}
                    rowsPerPageOptions={[]}
                />
            </Box>
            <CategoryModal row={row} open={isModalOpen} onClose={handleCloseManageRow} />
        </Box>
    );
}
